import { TriangleAlert } from "lucide-react";
import { formatOptionalHours } from "@/lib/persons/project-format";
import { type PersonProjectAssignment, stripAccountPrefix } from "@/lib/persons/project-guidance";
import { cn } from "@/lib/utils";

/**
 * One project, reduced to what a manager scans for: who it's for, and how the
 * selected month's guided hours compare to what was billed. Everything else —
 * the SOW name, status, role, totals, the month-by-month strip — lives in the
 * detail popup this card opens, so a person with eight assignments is a list
 * that can be read at a glance rather than eight blocks of detail.
 */
export function ProjectCard({
  assignment,
  month,
  monthLabel,
  showSow,
  onOpen,
}: {
  assignment: PersonProjectAssignment;
  /** Selected month key, `YYYY-MM` — driven by the summary banner above. */
  month: string;
  monthLabel: string;
  /**
   * Whether another card in view carries the same headline. One account
   * routinely runs several SOWs at once, and a person on four of them would
   * otherwise get four identical cards — so the SOW name is pulled back out of
   * the popup exactly when it is what tells them apart.
   */
  showSow: boolean;
  onOpen: () => void;
}) {
  const { accountName, demandName, months, guidance } = assignment;
  const entry = months.find((m) => m.month === month);

  // Only flagged on an active assignment: guidance is forward-looking, so a
  // rolled-off SOW having none is expected, and flagging it there would put a
  // warning on every previous project.
  const unguided = assignment.isActiveNow && !guidance;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex h-full w-full flex-col gap-3 rounded-xl border bg-card p-4 text-left shadow-sm",
        "transition-shadow hover:shadow-md",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      )}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        {/* The account is the headline; a demand with no account falls back to
            its own name rather than rendering an empty line. */}
        <div className="min-w-0">
          <p className="truncate font-semibold leading-tight">{accountName ?? demandName}</p>
          {showSow && accountName && (
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {stripAccountPrefix(demandName, accountName)}
            </p>
          )}
        </div>
        {unguided && (
          // The icon is decorative; the name it carries goes in real text so a
          // screen reader announces the warning rather than an unlabelled svg.
          <span className="mt-0.5 shrink-0 text-warning-foreground">
            <TriangleAlert className="size-4" aria-hidden="true" />
            <span className="sr-only">No guidance set</span>
          </span>
        )}
      </div>

      <div className="mt-auto grid grid-cols-2 gap-2">
        <Figure label={`Guided · ${monthLabel}`} value={formatOptionalHours(entry?.hours)} />
        <Figure label={`Billed · ${monthLabel}`} value={formatOptionalHours(entry?.loggedHours)} />
      </div>
    </button>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-secondary px-3 py-2">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-semibold leading-tight tabular-nums">{value}</div>
    </div>
  );
}
